import { useState } from 'react';

interface UploadVideoModalProps {
  onClose: () => void;
}

export default function UploadVideoModal({ onClose }: UploadVideoModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'highlights',
    videoFile: null as File | null,
    thumbnail: null as File | null,
    visibility: 'public'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
      
      setTimeout(() => {
        onClose();
      }, 2000);
    }, 1500);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'video' | 'thumbnail') => {
    if (e.target.files && e.target.files[0]) {
      if (type === 'video') {
        setFormData({ ...formData, videoFile: e.target.files[0] });
      } else {
        setFormData({ ...formData, thumbnail: e.target.files[0] });
      }
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <i className="ri-upload-2-line text-red-600 mr-3"></i>
            Upload Video
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {submitSuccess ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-4xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">Video Uploaded!</h3>
            <p className="text-gray-400">
              Your video is being processed. It will be available shortly!
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Video File <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-8 text-center hover:border-red-600 transition-colors">
                <input
                  type="file"
                  required
                  accept="video/*"
                  onChange={(e) => handleFileChange(e, 'video')}
                  className="hidden"
                  id="video-upload"
                />
                <label htmlFor="video-upload" className="cursor-pointer">
                  <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-upload-cloud-2-line text-white text-3xl"></i>
                  </div>
                  {formData.videoFile ? (
                    <p className="text-white font-semibold mb-2">{formData.videoFile.name}</p>
                  ) : (
                    <>
                      <p className="text-white font-semibold mb-2">Click to upload video</p>
                      <p className="text-gray-400 text-sm">MP4, MOV, AVI (Max 2GB)</p>
                    </>
                  )}
                </label>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Thumbnail <span className="text-gray-500 text-sm font-normal">(Optional)</span>
              </label>
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-red-600 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'thumbnail')}
                  className="hidden"
                  id="thumbnail-upload"
                />
                <label htmlFor="thumbnail-upload" className="cursor-pointer">
                  <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                    <i className="ri-image-line text-white text-2xl"></i>
                  </div>
                  {formData.thumbnail ? (
                    <p className="text-white text-sm">{formData.thumbnail.name}</p>
                  ) : (
                    <p className="text-gray-400 text-sm">Upload custom thumbnail (JPG, PNG)</p>
                  )}
                </label>
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Video Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Epic Comeback Victory"
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm"
              />
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none pr-8 text-sm cursor-pointer"
              >
                <option value="highlights">Highlights</option>
                <option value="tutorial">Tutorial</option>
                <option value="tournament">Tournament Match</option>
                <option value="analysis">Analysis</option>
                <option value="funny">Funny Moments</option>
                <option value="montage">Montage</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your video..."
                maxLength={500}
                rows={4}
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none resize-none text-sm"
              />
              <div className="text-right text-gray-500 text-sm mt-1">
                {formData.description.length}/500
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Visibility
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, visibility: 'public' })}
                  className={`py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    formData.visibility === 'public'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-900 text-gray-400 border border-gray-700'
                  }`}
                >
                  <i className="ri-global-line mr-2"></i>
                  Public
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, visibility: 'unlisted' })}
                  className={`py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    formData.visibility === 'unlisted'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-900 text-gray-400 border border-gray-700'
                  }`}
                >
                  <i className="ri-link mr-2"></i>
                  Unlisted
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, visibility: 'private' })}
                  className={`py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    formData.visibility === 'private'
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-900 text-gray-400 border border-gray-700'
                  }`}
                >
                  <i className="ri-lock-line mr-2"></i>
                  Private
                </button>
              </div>
            </div>

            <div className="bg-gray-900 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <i className="ri-information-line text-red-400 text-xl mr-3 mt-1"></i>
                <div>
                  <p className="text-white font-semibold mb-1">Processing Time</p>
                  <p className="text-gray-400 text-sm">
                    Videos are processed in HD quality. Processing time depends on video length and may take a few minutes.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ri-upload-2-line mr-2"></i>
                    Upload Video
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
